import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandPlan, BrandPlanSchedule, SuggestionRequestStatus } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Channels } from 'components/FirstTimeExperienceModal/steps/WhereToPublish/constants'
import { fetchBrandPlan } from './useBrandPlan'

interface Data {
  brand_plan: BrandPlan
  first_schedule?: BrandPlanSchedule
}

interface MutationVariables {
  growth_goals_input?: string
  key_dates_input?: string
  start_date?: string
  channels?: Array<Channels>
  signal?: AbortSignal
}

export interface Props {
  brandPlanId: number
}

export const startBrandPlan = async ({
  brandPlanId,
  workspaceId,
  growth_goals_input,
  key_dates_input,
  start_date,
  channels,
  signal,
}: {
  brandPlanId: number
  workspaceId: number
  growth_goals_input?: string
  key_dates_input?: string
  start_date?: string
  channels?: Array<Channels>
  signal?: AbortSignal
}) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_plans/${brandPlanId}/start`, {
    method: 'POST',
    body: { growth_goals_input, key_dates_input, start_date, channels },
    signal,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data.error)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<Data, Error, MutationVariables>, 'mutationFn'> {}

export const useStartBrandPlan = ({ brandPlanId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, MutationVariables>(
    async ({ growth_goals_input, key_dates_input, start_date, channels, signal }: MutationVariables = {}) => {
      let { brand_plan, first_schedule } = await startBrandPlan({
        workspaceId,
        brandPlanId,
        growth_goals_input,
        key_dates_input,
        start_date,
        channels,
        signal,
      })

      const interval = 500

      while (!brand_plan.started_at) {
        if (signal?.aborted) {
          throw new DOMException('The operation was aborted.', 'AbortError')
        }

        brand_plan = await queryClient.fetchQuery(['workspaces', workspaceId, 'brandPlans', 'current'], () =>
          fetchBrandPlan({ workspaceId, id: brandPlanId, signal }),
        )

        if (brand_plan.generation_status === SuggestionRequestStatus.Failed) {
          throw new Error('Failed to generate campaigns')
        }

        if (brand_plan.generation_status === SuggestionRequestStatus.Aborted) {
          throw new Error('Campaign generation was aborted')
        }

        await new Promise((resolve) => {
          setTimeout(resolve, interval)
        })
      }

      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandPlans'])

      return { brand_plan, first_schedule }
    },
    options,
  )
}
